import { Card, Suit, SeatIndex, HandState, BidValue, Ruleset, DEFAULT_RULESET, TEAM_OF } from "../types/game";
import { legalBids, legalPlays } from "../engine/engine";
import { cardEquals, GAME_POINTS } from "../engine/deck";

export type Difficulty = "advanced" | "expert";

/** Coerce stored / legacy difficulty values (solo + Firestore): only expert tweaks bidding. */
export function normalizeDifficulty(raw: unknown): Difficulty {
  return raw === "expert" ? "expert" : "advanced";
}

// ============================================================
// Public API
// ============================================================

export function botBid(
  hand: HandState,
  seat: SeatIndex,
  difficulty: Difficulty
): BidValue | "pass" {
  return heuristicBid(hand, seat, difficulty === "expert" ? 0.5 : 1);
}

export function botChooseTrump(hand: HandState, seat: SeatIndex): Suit {
  // Pick the suit with the most cards; break ties by highest-ranked honor.
  const cards = hand.hands[seat];
  const counts: Record<Suit, Card[]> = { C: [], D: [], H: [], S: [] };
  for (const c of cards) counts[c.suit].push(c);
  const suits: Suit[] = ["C", "D", "H", "S"];
  suits.sort((a, b) => {
    if (counts[b].length !== counts[a].length) return counts[b].length - counts[a].length;
    const maxA = counts[a].reduce((m, c) => Math.max(m, c.rank), 0);
    const maxB = counts[b].reduce((m, c) => Math.max(m, c.rank), 0);
    return maxB - maxA;
  });
  return suits[0];
}

export function botPlay(
  hand: HandState,
  seat: SeatIndex,
  difficulty: Difficulty,
  ruleset: Ruleset = DEFAULT_RULESET,
): Card {
  const legal = legalPlays(hand, seat, ruleset);
  if (legal.length === 0) throw new Error("No legal plays");
  if (legal.length === 1) return legal[0];
  // Advanced vs expert differ only on bidding aggression; card play uses one engine.
  void difficulty;
  return advancedPlay(hand, seat, legal);
}

// ============================================================
// Bidding heuristic (shared tiers)
// ============================================================

function heuristicBid(
  hand: HandState,
  _seat: SeatIndex,
  _aggression: number = 1
): BidValue | "pass" {
  const legal = legalBids(hand);
  const cards = hand.hands[_seat];
  const strength = bestSuitStrength(cards);
  const t = thresholdForAggression(_aggression);
  // Rough thresholds tuned to avg hand power
  if (strength >= t.four && legal.includes(4)) return 4;
  if (strength >= t.three && legal.includes(3)) return 3;
  if (strength >= t.two && legal.includes(2)) return 2;
  return "pass";
}

function thresholdForAggression(agg: number): { two: number; three: number; four: number } {
  const d = agg < 1 ? 1 : 0;
  return { two: 8 - d, three: 12 - d, four: 16 - d };
}

/** Core trick logic (following + most leads); advancedPlay adds opening-lead / jack tweaks. */
function corePlay(hand: HandState, seat: SeatIndex, legal: Card[]): Card {
  const trump = hand.trump!;
  const trick = hand.currentTrick;

  // Leading?
  if (!trick || trick.plays.length === 0) {
    const trumps = legal.filter(c => c.suit === trump);
    const offs = legal.filter(c => c.suit !== trump);
    // Long trump: pull from the top regardless of declaring (shorten games / draw power).
    if (trumps.length >= 3) {
      return trumps.sort((a, b) => b.rank - a.rank)[0];
    }
    if (isDeclaringSeat(hand, seat)) {
      // Defenders out of trump: cash game before running our last trump.
      if (offs.length > 0 && defendersCannotTrump(hand, trump)) {
        const cash = pickBestGameCashLead(offs);
        if (cash) return cash;
      }
      // Still drawing: run high trump when we have any.
      if (trumps.length > 0) {
        return trumps.sort((a, b) => b.rank - a.rank)[0];
      }
    }
    const offSuit = legal.filter(c => c.suit !== trump);
    if (offSuit.length > 0) {
      return offSuit.sort((a, b) => a.rank - b.rank)[0]; // lowest off-suit
    }
    return legal.sort((a, b) => a.rank - b.rank)[0];
  }

  // Following — win the trick as cheaply as possible; ruff with trump when a lower trump
  // wins than the cheapest in-suit winner (saves honors / takes with power). When losing,
  // we never discretionary-trump (see pickDumpLosers).
  const ledSuit = trick.plays[0].card.suit;
  const highestPlayed = highestWinningCard(trick.plays.map(p => p.card), trump, ledSuit);

  const winners = legal.filter(c => beats(c, highestPlayed, trump, ledSuit));
  if (winners.length > 0) {
    const winChoice = pickCheapestWinner(winners, ledSuit, trump);
    const losers = legal.filter(c => !beats(c, highestPlayed, trump, ledSuit));

    // Going last: avoid burning Q/K/A of trump on worthless tricks, or overruffing partner with honors.
    const lastToPlay = trick.plays.length === 3;
    if (losers.length > 0 && lastToPlay && honorTrump(winChoice, trump)) {
      const stake = gameStakeOnTable(trick.plays.map(p => p.card));
      const takingSeat = winningSeatPlay(trick.plays, trump, ledSuit);
      const partnerWinning =
        takingSeat !== null &&
        takingSeat.seat !== seat &&
        TEAM_OF[takingSeat.seat] === TEAM_OF[seat];
      const contestingJackOfTrump =
        highestPlayed.suit === trump && highestPlayed.rank === 11;

      const duckHonor =
        !contestingJackOfTrump &&
        (partnerWinning || stake === 0);

      if (duckHonor) {
        return pickDumpLosers(hand, seat, losers, trump, ledSuit);
      }
    }

    // Partner holds boss in the led suit and defenders can't trump: sluff instead of ruffing
    // unless we want the lead (bidder or a high-stake trick).
    if (
      losers.length > 0 &&
      ledSuit !== trump &&
      defendersCannotTrump(hand, trump) &&
      partnerWinningBossInLedSuit(hand, seat, trump, ledSuit) &&
      winChoice.suit === trump &&
      !shouldTakeLead(hand, seat, trump, ledSuit)
    ) {
      return pickDumpLosers(hand, seat, losers, trump, ledSuit);
    }

    return winChoice;
  }
  return pickDumpLosers(hand, seat, legal, trump, ledSuit);
}

// ============================================================
// Advanced — Monte Carlo (simplified for v1)
// ============================================================

function advancedPlay(hand: HandState, seat: SeatIndex, legal: Card[]): Card {
  // Jack preservation / capture on top of core heuristics.
  const trump = hand.trump!;
  const trick = hand.currentTrick;
  const firstLeadByBidder =
    hand.bidder === seat &&
    hand.tricks.length === 0 &&
    !!trick &&
    trick.plays.length === 0;

  // Opening lead in advanced mode: when the bidder starts the hand, avoid
  // burning the Jack/2 of trump unless those are the only legal leads.
  if (firstLeadByBidder) {
    const saferTrumpLeads = legal.filter(
      c => c.suit === trump && c.rank !== 11 && c.rank !== 2 && c.rank !== 10,
    );
    if (saferTrumpLeads.length > 0) {
      return saferTrumpLeads.sort((a, b) => b.rank - a.rank)[0];
    }
  }

  const ourJack = legal.find(c => c.suit === trump && c.rank === 11);

  // If opponent's high card is on the table and we can capture with the jack, do it
  // (we want the jack point). But don't waste the jack if it's not needed.
  if (trick && trick.plays.length > 0 && ourJack) {
    const ledSuit = trick.plays[0].card.suit;
    const highestPlayed = highestWinningCard(trick.plays.map(p => p.card), trump, ledSuit);
    // If our jack wins this trick, consider whether anything cheaper wins
    const cheaperWinners = legal.filter(
      c => c.rank !== 11 && c.suit === trump && beats(c, highestPlayed, trump, ledSuit),
    );
    if (cheaperWinners.length === 0 && beats(ourJack, highestPlayed, trump, ledSuit)) {
      // Only our jack can win — play it
      return ourJack;
    }
  }

  return corePlay(hand, seat, legal);
}

// ============================================================
// Helpers
// ============================================================

/** Solo has all four hands; online bots only see their own cards. */
function hasFullHandVisibility(hand: HandState): boolean {
  return hand.hands.filter(h => h.length > 0).length > 1;
}

function defenderSeats(hand: HandState): SeatIndex[] {
  if (hand.bidder === undefined) return [];
  const bidTeam = TEAM_OF[hand.bidder];
  const out: SeatIndex[] = [];
  for (let si = 0; si < 4; si++) {
    const s = si as SeatIndex;
    if (TEAM_OF[s] !== bidTeam) out.push(s);
  }
  return out;
}

function countTrumpInSeats(hand: HandState, seats: SeatIndex[], trump: Suit): number {
  let n = 0;
  for (const s of seats) {
    for (const c of hand.hands[s]) {
      if (c.suit === trump) n++;
    }
  }
  return n;
}

/**
 * True when defenders cannot ruff a side-suit lead (full hands show no trump,
 * or both defenders have shown void on a trump lead / pitched 2 or J of trump).
 */
function defendersCannotTrump(hand: HandState, trump: Suit): boolean {
  const defenders = defenderSeats(hand);
  if (defenders.length === 0) return false;
  if (hasFullHandVisibility(hand)) {
    return countTrumpInSeats(hand, defenders, trump) === 0;
  }
  return inferBothDefendersVoidTrump(hand, trump, defenders);
}

/** From completed + current tricks when trump was led. */
function inferBothDefendersVoidTrump(
  hand: HandState,
  trump: Suit,
  defenders: SeatIndex[],
): boolean {
  const voidOnTrump = new Set<SeatIndex>();
  const tricks = [...hand.tricks];
  if (hand.currentTrick) tricks.push(hand.currentTrick);
  for (const t of tricks) {
    if (t.plays.length === 0) continue;
    const led = t.plays[0].card.suit;
    if (led !== trump) continue;
    for (const p of t.plays) {
      if (!defenders.includes(p.seat)) continue;
      if (p.card.suit !== trump) {
        voidOnTrump.add(p.seat);
      } else if (p.card.rank === 2 || p.card.rank === 11) {
        // Void sluff: pitched 2 or J of trump on a trump lead.
        voidOnTrump.add(p.seat);
      }
    }
  }
  return defenders.every(s => voidOnTrump.has(s));
}

/** Bidder or a heavy game trick: worth overtrumping partner to seize the lead. */
function shouldTakeLead(
  hand: HandState,
  seat: SeatIndex,
  _trump: Suit,
  _ledSuit: Suit,
): boolean {
  const trick = hand.currentTrick;
  if (!trick) return false;
  if (hand.bidder === seat) return true;
  const stake = gameStakeOnTable(trick.plays.map(p => p.card));
  return stake >= 10;
}

/** Best offsuit card to bank game when we won't be ruffed (defenders trump count === 0). */
function pickBestGameCashLead(offsuit: Card[]): Card | undefined {
  const tagged = offsuit.filter(c => GAME_POINTS[c.rank] > 0);
  if (tagged.length === 0) return undefined;
  return [...tagged].sort((a, b) =>
    GAME_POINTS[b.rank] - GAME_POINTS[a.rank] || b.rank - a.rank)[0];
}

function isDeclaringSeat(hand: HandState, seat: SeatIndex): boolean {
  return hand.bidder !== undefined && TEAM_OF[seat] === TEAM_OF[hand.bidder];
}

/** Q, K, or A of trump — worth holding for later fights (e.g. over J) vs empty tricks. */
function honorTrump(card: Card, trumpSuit: Suit): boolean {
  return card.suit === trumpSuit && card.rank >= 12 && card.rank <= 14;
}

function gameStakeOnTable(cards: Card[]): number {
  let s = 0;
  for (const c of cards) s += GAME_POINTS[c.rank];
  return s;
}

function winningSeatPlay(
  plays: { seat: SeatIndex; card: Card }[],
  trump: Suit,
  ledSuit: Suit,
): { seat: SeatIndex; card: Card } | null {
  if (plays.length === 0) return null;
  const hi = highestWinningCard(plays.map(p => p.card), trump, ledSuit);
  for (let i = plays.length - 1; i >= 0; i--) {
    if (cardEquals(plays[i].card, hi)) return plays[i];
  }
  return plays[plays.length - 1];
}

/** Ranks of a suit already seen this hand (completed tricks + current trick). */
function playedSuitRanksSeen(hand: HandState, suit: Suit): Set<number> {
  const ranks = new Set<number>();
  for (const t of hand.tricks) {
    for (const p of t.plays) {
      if (p.card.suit === suit) ranks.add(p.card.rank);
    }
  }
  const ct = hand.currentTrick;
  if (ct) {
    for (const p of ct.plays) {
      if (p.card.suit === suit) ranks.add(p.card.rank);
    }
  }
  return ranks;
}

function playedTrumpRanksSeen(hand: HandState): Set<number> {
  return playedSuitRanksSeen(hand, hand.trump!);
}

/**
 * True if `card` is in `suit` and every higher rank in that suit has already been played.
 */
function suitCardIsPublicBoss(card: Card, suit: Suit, playedRanks: Set<number>): boolean {
  if (card.suit !== suit) return false;
  for (let r = card.rank + 1; r <= 14; r++) {
    if (!playedRanks.has(r)) return false;
  }
  return true;
}

function trumpCardIsPublicBoss(card: Card, trump: Suit, playedRanks: Set<number>): boolean {
  return suitCardIsPublicBoss(card, trump, playedRanks);
}

/** Partner is currently winning the trick on our team. */
function partnerIsWinning(hand: HandState, seat: SeatIndex, trump: Suit, ledSuit: Suit): boolean {
  const trick = hand.currentTrick;
  if (!trick || trick.plays.length === 0) return false;
  const win = winningSeatPlay(trick.plays, trump, ledSuit);
  return win !== null && win.seat !== seat && TEAM_OF[win.seat] === TEAM_OF[seat];
}

/** Partner is winning with a card that cannot be beaten in the led suit (trump or side suit). */
function partnerWinningBossInLedSuit(
  hand: HandState,
  seat: SeatIndex,
  trump: Suit,
  ledSuit: Suit,
): boolean {
  const trick = hand.currentTrick;
  if (!trick || trick.plays.length === 0) return false;
  const hi = highestWinningCard(trick.plays.map(p => p.card), trump, ledSuit);
  const win = winningSeatPlay(trick.plays, trump, ledSuit);
  if (!win || win.seat === seat) return false;
  if (TEAM_OF[win.seat] !== TEAM_OF[seat]) return false;
  if (ledSuit === trump) {
    if (win.card.suit !== trump) return false;
    return trumpCardIsPublicBoss(hi, trump, playedTrumpRanksSeen(hand));
  }
  if (win.card.suit !== ledSuit) return false;
  return suitCardIsPublicBoss(hi, ledSuit, playedSuitRanksSeen(hand, ledSuit));
}

/**
 * Under partner’s boss trump, pitch a useful card: 2 → J → 10 of trump; if void, a 10 offsuit
 * from the shortest remaining holding (keeps long-suit 10s for late game).
 */
function pickHelpfulSluffToPartnerBossTrump(
  hand: HandState,
  seat: SeatIndex,
  legal: Card[],
  trump: Suit,
): Card | null {
  const nonTrump = legal.filter(c => c.suit !== trump);
  if (nonTrump.length > 0) {
    const myHand = hand.hands[seat];
    const tens = nonTrump.filter(c => c.rank === 10);
    if (tens.length > 0) {
      const scored = tens.map(c => ({
        c,
        n: myHand.filter(x => x.suit === c.suit && !cardEquals(x, c)).length,
      }));
      scored.sort((a, b) => a.n - b.n || a.c.suit.localeCompare(b.c.suit));
      return scored[0].c;
    }
    return [...nonTrump].sort(sortDumpGame)[0];
  }
  const trumps = legal.filter(c => c.suit === trump);
  if (trumps.length > 0) {
    const two = trumps.find(c => c.rank === 2);
    if (two) return two;
    const jack = trumps.find(c => c.rank === 11);
    if (jack) return jack;
    const ten = trumps.find(c => c.rank === 10);
    if (ten) return ten;
    return [...trumps].sort((a, b) => a.rank - b.rank)[0];
  }
  return null;
}

/** Under partner's non-boss trump pull, follow with high non-guard trump to keep drawing. */
function pickPartnerTrumpPullFollow(legal: Card[], trump: Suit): Card | null {
  const trumps = legal.filter(c => c.suit === trump);
  if (trumps.length === 0) return null;
  const pullers = trumps.filter(c => !isGuardTrump(c, trump));
  if (pullers.length > 0) {
    return [...pullers].sort((a, b) => b.rank - a.rank)[0];
  }
  return [...trumps].sort((a, b) => b.rank - a.rank)[0];
}

/** 2 / 10 / J of trump — hold when sluffing until junk trump is gone. */
function isGuardTrump(card: Card, trumpSuit: Suit): boolean {
  return card.suit === trumpSuit && (card.rank === 2 || card.rank === 10 || card.rank === 11);
}

/**
 * Among cards that win the trick: prefer the cheapest **rank** to win.
 * If both in-suit and trump can win, **ruff** when the lowest winning trump
 * is lower rank than the lowest winning card in the led suit — classic
 * “take it with trump” without burning a high card in the suit.
 * Tie (same rank): ruff to stay aggressive with trump.
 */
function pickCheapestWinner(winners: Card[], ledSuit: Suit, trump: Suit): Card {
  const inLed = winners.filter(c => c.suit === ledSuit);
  const trumps = winners.filter(c => c.suit === trump);
  if (inLed.length > 0 && trumps.length > 0) {
    const bestIn = [...inLed].sort((a, b) => a.rank - b.rank)[0];
    const bestT = [...trumps].sort((a, b) => a.rank - b.rank)[0];
    if (bestT.rank < bestIn.rank) return bestT;
    if (bestIn.rank < bestT.rank) return bestIn;
    return bestT;
  }
  if (inLed.length > 0) return [...inLed].sort((a, b) => a.rank - b.rank)[0];
  if (trumps.length > 0) return [...trumps].sort((a, b) => a.rank - b.rank)[0];
  return [...winners].sort((a, b) => a.rank - b.rank)[0];
}

function sortDumpGame(a: Card, b: Card): number {
  return (GAME_POINTS[a.rank] - GAME_POINTS[b.rank]) || (a.rank - b.rank);
}

/** When forced to lose, prefer sluffing junk trump before 2/10/J of trump. */
function compareDumpTrump(a: Card, b: Card, trumpSuit: Suit): number {
  const ga = isGuardTrump(a, trumpSuit) ? 1 : 0;
  const gb = isGuardTrump(b, trumpSuit) ? 1 : 0;
  if (ga !== gb) return ga - gb;
  return sortDumpGame(a, b);
}

/**
 * Loser trick: if we can follow the led suit, never discretionary-trump — dump in suit only.
 * If trump led, dump low “junk” trump before throwing 2/10/J. If void, dump offsuit before trump.
 */
function pickDumpLosers(
  hand: HandState,
  seat: SeatIndex,
  legal: Card[],
  trump: Suit,
  ledSuit: Suit,
): Card {
  const holdLedSuit = ledSuit !== trump && legal.some(c => c.suit === ledSuit);
  if (holdLedSuit) {
    const inLed = legal.filter(c => c.suit === ledSuit);
    return [...inLed].sort(sortDumpGame)[0];
  }
  if (partnerWinningBossInLedSuit(hand, seat, trump, ledSuit)) {
    if (ledSuit === trump) {
      const helpful = pickHelpfulSluffToPartnerBossTrump(hand, seat, legal, trump);
      if (helpful) return helpful;
    } else {
      const nonTrump = legal.filter(c => c.suit !== trump);
      if (nonTrump.length > 0) {
        return [...nonTrump].sort(sortDumpGame)[0];
      }
    }
  }
  if (ledSuit === trump) {
    if (
      partnerIsWinning(hand, seat, trump, ledSuit) &&
      !partnerWinningBossInLedSuit(hand, seat, trump, ledSuit)
    ) {
      const offs = legal.filter(c => c.suit !== trump);
      if (offs.length > 0) {
        return [...offs].sort(sortDumpGame)[0];
      }
      const pull = pickPartnerTrumpPullFollow(legal, trump);
      if (pull) return pull;
    }
    return [...legal].sort((a, b) => compareDumpTrump(a, b, trump))[0];
  }
  const nonTrump = legal.filter(c => c.suit !== trump);
  if (nonTrump.length > 0) {
    return [...nonTrump].sort(sortDumpGame)[0];
  }
  return [...legal].sort((a, b) => compareDumpTrump(a, b, trump))[0];
}

function bestSuitStrength(cards: Card[]): number {
  // Strength formula: each card in suit = 1; A/K/Q/J honors get bonuses; length bonus.
  const bySuit: Record<Suit, Card[]> = { C: [], D: [], H: [], S: [] };
  for (const c of cards) bySuit[c.suit].push(c);
  let best = 0;
  for (const suit of ["C", "D", "H", "S"] as Suit[]) {
    const s = bySuit[suit];
    let score = s.length * 2; // length
    for (const c of s) {
      if (c.rank === 14) score += 4;
      if (c.rank === 13) score += 3;
      if (c.rank === 12) score += 2;
      if (c.rank === 11) score += 2;
      if (c.rank === 2) score += 1; // low guaranteed
    }
    if (score > best) best = score;
  }
  return best;
}

function beats(candidate: Card, current: Card, trump: Suit, ledSuit: Suit): boolean {
  if (candidate.suit === trump && current.suit !== trump) return true;
  if (candidate.suit === trump && current.suit === trump) return candidate.rank > current.rank;
  if (candidate.suit !== trump && current.suit === trump) return false;
  if (candidate.suit === ledSuit && current.suit === ledSuit) return candidate.rank > current.rank;
  return false;
}

function highestWinningCard(cards: Card[], trump: Suit, ledSuit: Suit): Card {
  let best = cards[0];
  for (const c of cards.slice(1)) {
    if (beats(c, best, trump, ledSuit)) best = c;
  }
  return best;
}
